import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import {
  DrawerContentComponentProps,
} from '@react-navigation/drawer';
import { CommonActions } from '@react-navigation/native';
import Svg, { Path } from 'react-native-svg';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import ConfirmModal from './ConfirmModal';
import { COLORS, FONTS } from '../constants/config';

// Logo images - horizontal logo with text
const logoHorizontal = require('../../assets/logo/logo-horizental.png'); // Colored logo for light mode
const logoHorizontalWhite = require('../../assets/logo/logo-Horizental-white.png'); // White logo for dark mode

// ============================================================
// Inline SVG Icon Components
// ============================================================

const StarIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const LayoutGridIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 3H4C3.44772 3 3 3.44772 3 4V9C3 9.55228 3.44772 10 4 10H9C9.55228 10 10 9.55228 10 9V4C10 3.44772 9.55228 3 9 3Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 3H15C14.4477 3 14 3.44772 14 4V9C14 9.55228 14.4477 10 15 10H20C20.5523 10 21 9.55228 21 9V4C21 3.44772 20.5523 3 20 3Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 14H15C14.4477 14 14 14.4477 14 15V20C14 20.5523 14.4477 21 15 21H20C20.5523 21 21 20.5523 21 20V15C21 14.4477 20.5523 14 20 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 14H4C3.44772 14 3 14.4477 3 15V20C3 20.5523 3.44772 21 4 21H9C9.55228 21 10 20.5523 10 20V15C10 14.4477 9.55228 14 9 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BuildingIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V4C20 2.89543 19.1046 2 18 2Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 22V18H15V22" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 6H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 10H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 10H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 14H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 10H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 14H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TargetIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const RocketIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4.5 16.4993C3 17.7593 2.5 21.4993 2.5 21.4993C2.5 21.4993 6.24 20.9993 7.5 19.4993C8.21 18.6593 8.2 17.3693 7.41 16.5893C7.02131 16.2184 6.50929 16.004 5.97223 15.9874C5.43516 15.9708 4.91088 16.1531 4.5 16.4993Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15.0002L9 12.0002C9.53214 10.6197 10.2022 9.29631 11 8.05025C12.1652 6.18723 13.7876 4.6533 15.713 3.59434C17.6384 2.53538 19.8027 1.98662 22 2.00025C22 4.72025 21.22 9.50025 16 13.0002C14.7369 13.799 13.3968 14.469 12 15.0002Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11.9995H4C4 11.9995 4.55 8.96953 6 7.99953C7.62 6.91953 11 7.99953 11 7.99953" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V20C12 20 15.03 19.45 16 18C17.08 16.38 16 13 16 13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const GitBranchIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 3V15" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18 9C19.6569 9 21 7.65685 21 6C21 4.34315 19.6569 3 18 3C16.3431 3 15 4.34315 15 6C15 7.65685 16.3431 9 18 9Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 21C7.65685 21 9 19.6569 9 18C9 16.3431 7.65685 15 6 15C4.34315 15 3 16.3431 3 18C3 19.6569 4.34315 21 6 21Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18 9C18 11.3869 17.0518 13.6761 15.364 15.364C13.6761 17.0518 11.3869 18 9 18" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const WorkflowIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 3H5C3.89543 3 3 3.89543 3 5V9C3 10.1046 3.89543 11 5 11H9C10.1046 11 11 10.1046 11 9V5C11 3.89543 10.1046 3 9 3Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 11V15C7 15.5304 7.21071 16.0391 7.58579 16.4142C7.96086 16.7893 8.46957 17 9 17H13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 13H15C13.8954 13 13 13.8954 13 15V19C13 20.1046 13.8954 21 15 21H19C20.1046 21 21 20.1046 21 19V15C21 13.8954 20.1046 13 19 13Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const HandServicesIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 16.0566H13C13.5304 16.0566 14.0391 15.8459 14.4142 15.4709C14.7893 15.0958 15 14.5871 15 14.0566C15 13.5262 14.7893 13.0175 14.4142 12.6424C14.0391 12.2674 13.5304 12.0566 13 12.0566H10C9.4 12.0566 8.9 12.2566 8.6 12.6566L3 18.0566" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 22.0569L8.6 20.6569C8.9 20.2569 9.4 20.0569 10 20.0569H14C15.1 20.0569 16.1 19.6569 16.8 18.8569L21.4 14.4569C21.7859 14.0922 22.0111 13.5892 22.0261 13.0585C22.0411 12.5278 21.8447 12.0128 21.48 11.6269C21.1153 11.241 20.6123 11.0158 20.0816 11.0008C19.5508 10.9858 19.0359 11.1822 18.65 11.5469L14.45 15.4469" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2.5 17.5L7 22.5" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7.90077 11.0993L7.65241 10.8927C7.47666 10.7474 7.26388 10.6451 7.03547 10.5962C6.80705 10.5473 6.57105 10.5534 6.35118 10.6141L6.24354 10.6401C5.91169 10.7315 5.54793 10.6976 5.23212 10.546C4.91631 10.3944 4.67425 10.1374 4.55908 9.83143L4.46369 9.57783C4.34911 9.27169 4.37089 8.9418 4.52425 8.66057C4.6776 8.37935 4.95 8.16978 5.28165 8.07786L5.39055 8.04596C5.61126 7.9851 5.8079 7.87152 5.96054 7.71672C6.11318 7.56192 6.21642 7.37139 6.25979 7.16444L6.32386 6.86522C6.36635 6.66015 6.34859 6.44625 6.27237 6.24478C6.19614 6.0433 6.0641 5.86127 5.88936 5.71677L5.80433 5.6372C5.53941 5.41752 5.37673 5.11477 5.35199 4.79541C5.32725 4.47605 5.44248 4.16617 5.67239 3.9338L5.86327 3.74151C6.09364 3.50953 6.41972 3.3742 6.76993 3.36521C7.12013 3.35623 7.46584 3.47434 7.73117 3.6936L7.81871 3.76144C7.99447 3.90679 8.20724 4.00906 8.43566 4.05797C8.66407 4.10689 8.90007 4.10073 9.11994 4.0401L9.43111 3.95334C9.65076 3.89278 9.84658 3.77999 9.99893 3.6263C10.1513 3.4726 10.2548 3.28341 10.2991 3.07769L10.3217 2.97208C10.3884 2.66088 10.5894 2.39178 10.8805 2.22398C11.1717 2.05619 11.5291 2.00344 11.8742 2.07735L12.1605 2.13866C12.5056 2.21256 12.8101 2.40707 13.007 2.67938C13.2039 2.9517 13.2771 3.27951 13.2104 3.59072L13.1878 3.69632C13.144 3.90214 13.1609 4.11714 13.237 4.31974C13.313 4.52235 13.4455 4.70544 13.6211 4.85064L13.8694 5.05723C14.0452 5.20258 14.258 5.30485 14.4864 5.35377C14.7148 5.40268 14.9508 5.39652 15.1707 5.33589L15.2783 5.30986C15.6102 5.21848 15.9739 5.25231 16.2897 5.40393C16.6055 5.55554 16.8476 5.81254 16.9628 6.11852L17.0581 6.37212C17.1727 6.67826 17.151 7.00816 16.9976 7.28938C16.8442 7.5706 16.5718 7.78017 16.2402 7.87209L16.1313 7.904C15.9106 7.96485 15.7139 8.07843 15.5613 8.23323C15.4087 8.38803 15.3054 8.57856 15.2621 8.78552L15.1992 9.07886C15.154 9.28543 15.1702 9.50153 15.2461 9.70526C15.3219 9.909 15.4548 10.0931 15.6312 10.2391L15.7188 10.3069C15.9837 10.5266 16.1464 10.8293 16.1711 11.1487" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10.3417 9.92974C11.4217 10.161 12.4848 9.47297 12.7161 8.39289C12.9474 7.31281 12.2594 6.24973 11.1793 6.01842C10.0992 5.78712 9.03612 6.47519 8.80482 7.55527C8.57351 8.63535 9.26158 9.69843 10.3417 9.92974Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TriangleAlertIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21.7304 18.0002L13.7304 4.00022C13.556 3.69243 13.303 3.43641 12.9973 3.25829C12.6917 3.08017 12.3442 2.98633 11.9904 2.98633C11.6366 2.98633 11.2892 3.08017 10.9835 3.25829C10.6778 3.43641 10.4249 3.69243 10.2504 4.00022L2.25042 18.0002C2.0741 18.3056 1.98165 18.6521 1.98243 19.0047C1.98321 19.3573 2.0772 19.7035 2.25486 20.008C2.43253 20.3126 2.68757 20.5648 2.99411 20.7391C3.30066 20.9133 3.64783 21.0034 4.00042 21.0002H20.0004C20.3513 20.9999 20.6959 20.9072 20.9997 20.7315C21.3035 20.5558 21.5556 20.3033 21.7309 19.9993C21.9062 19.6954 21.9985 19.3506 21.9984 18.9997C21.9983 18.6488 21.9059 18.3041 21.7304 18.0002Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 9V13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 17H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DownloadIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 15V3" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 10L12 15L17 10" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Reports/Chart icon for التقارير
const FileChartIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M14.5 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7.5L14.5 2Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 18V13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18V11" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 18V15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CircleHelpIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9.08984 9.00057C9.32495 8.33224 9.789 7.76868 10.3998 7.4097C11.0106 7.05073 11.7287 6.91951 12.427 7.03928C13.1253 7.15906 13.7587 7.52209 14.2149 8.0641C14.6712 8.6061 14.9209 9.29209 14.9198 10.0006C14.9198 12.0006 11.9198 13.0006 11.9198 13.0006" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 17H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Settings/Administration icon
const SettingsIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12.22 2H11.78C11.2496 2 10.7409 2.21071 10.3658 2.58579C9.99072 2.96086 9.78 3.46957 9.78 4V4.18C9.77964 4.53073 9.68706 4.87519 9.51154 5.17884C9.33602 5.48248 9.08374 5.73464 8.78 5.91L8.35 6.16C8.04596 6.33554 7.70108 6.42795 7.35 6.42795C6.99893 6.42795 6.65404 6.33554 6.35 6.16L6.2 6.08C5.74107 5.81526 5.19584 5.74344 4.684 5.88031C4.17217 6.01717 3.73555 6.35154 3.47 6.81L3.25 7.19C2.98526 7.64893 2.91345 8.19416 3.05031 8.706C3.18717 9.21783 3.52154 9.65445 3.98 9.92L4.13 10.02C4.43228 10.1945 4.68362 10.4451 4.85905 10.7468C5.03448 11.0486 5.1279 11.391 5.13 11.74V12.25C5.1314 12.6024 5.03965 12.949 4.86405 13.2545C4.68844 13.5601 4.43521 13.8138 4.13 13.99L3.98 14.08C3.52154 14.3456 3.18717 14.7822 3.05031 15.294C2.91345 15.8058 2.98526 16.3511 3.25 16.81L3.47 17.19C3.73555 17.6485 4.17217 17.9828 4.684 18.1197C5.19584 18.2566 5.74107 18.1847 6.2 17.92L6.35 17.84C6.65404 17.6645 6.99893 17.5721 7.35 17.5721C7.70108 17.5721 8.04596 17.6645 8.35 17.84L8.78 18.09C9.08374 18.2654 9.33602 18.5175 9.51154 18.8212C9.68706 19.1248 9.77964 19.4693 9.78 19.82V20C9.78 20.5304 9.99072 21.0391 10.3658 21.4142C10.7409 21.7893 11.2496 22 11.78 22H12.22C12.7504 22 13.2591 21.7893 13.6342 21.4142C14.0093 21.0391 14.22 20.5304 14.22 20V19.82C14.2204 19.4693 14.3129 19.1248 14.4885 18.8212C14.664 18.5175 14.9163 18.2654 15.22 18.09L15.65 17.84C15.954 17.6645 16.2989 17.5721 16.65 17.5721C17.0011 17.5721 17.346 17.6645 17.65 17.84L17.8 17.92C18.2589 18.1847 18.8042 18.2566 19.316 18.1197C19.8278 17.9828 20.2645 17.6485 20.53 17.19L20.75 16.8C21.0147 16.3411 21.0866 15.7958 20.9497 15.284C20.8128 14.7722 20.4785 14.3356 20.02 14.07L19.87 13.99C19.5648 13.8138 19.3116 13.5601 19.136 13.2545C18.9604 12.949 18.8686 12.6024 18.87 12.25V11.75C18.8686 11.3976 18.9604 11.051 19.136 10.7455C19.3116 10.4399 19.5648 10.1862 19.87 10.01L20.02 9.92C20.4785 9.65445 20.8128 9.21783 20.9497 8.706C21.0866 8.19416 21.0147 7.64893 20.75 7.19L20.53 6.81C20.2645 6.35154 19.8278 6.01717 19.316 5.88031C18.8042 5.74344 18.2589 5.81526 17.8 6.08L17.65 6.16C17.346 6.33554 17.0011 6.42795 16.65 6.42795C16.2989 6.42795 15.954 6.33554 15.65 6.16L15.22 5.91C14.9163 5.73464 14.664 5.48248 14.4885 5.17884C14.3129 4.87519 14.2204 4.53073 14.22 4.18V4C14.22 3.46957 14.0093 2.96086 13.6342 2.58579C13.2591 2.21071 12.7504 2 12.22 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Calendar icon for Schedules
const CalendarIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 2V6" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 2V6" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M3 10H21" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 14H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 14H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 18H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// KPI / Chart icon for performance indicators
const ChartBarIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 20V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 20V14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Talents initiative icon (speech bubble with dots)
const TalentsIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C12.83 22 13.5 21.33 13.5 20.5C13.5 20.12 13.36 19.78 13.12 19.53C12.89 19.27 12.75 18.94 12.75 18.56C12.75 17.73 13.42 17.06 14.25 17.06H16C19.31 17.06 22 14.37 22 11.06C22 6.06 17.52 2 12 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7.5 11.5C8.05228 11.5 8.5 11.0523 8.5 10.5C8.5 9.94772 8.05228 9.5 7.5 9.5C6.94772 9.5 6.5 9.94772 6.5 10.5C6.5 11.0523 6.94772 11.5 7.5 11.5Z" fill={color}/>
    <Path d="M10.5 8C11.0523 8 11.5 7.55228 11.5 7C11.5 6.44772 11.0523 6 10.5 6C9.94772 6 9.5 6.44772 9.5 7C9.5 7.55228 9.94772 8 10.5 8Z" fill={color}/>
    <Path d="M15.5 8C16.0523 8 16.5 7.55228 16.5 7C16.5 6.44772 16.0523 6 15.5 6C14.9477 6 14.5 6.44772 14.5 7C14.5 7.55228 14.9477 8 15.5 8Z" fill={color}/>
    <Path d="M18.5 11.5C19.0523 11.5 19.5 11.0523 19.5 10.5C19.5 9.94772 19.0523 9.5 18.5 9.5C17.9477 9.5 17.5 9.94772 17.5 10.5C17.5 11.0523 17.9477 11.5 18.5 11.5Z" fill={color}/>
  </Svg>
);

// Organizations list icon (multiple buildings)
const BuildingsIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 22V4C6 3.46957 6.21071 2.96086 6.58579 2.58579C6.96086 2.21071 7.46957 2 8 2H16C16.5304 2 17.0391 2.21071 17.4142 2.58579C17.7893 2.96086 18 3.46957 18 4V22" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M3 22H21" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 6H14" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 10H14" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 14H14" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 18H14" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EllipsisVerticalIcon = ({ size = 18, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M11 19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M11 5C11 5.55228 11.4477 6 12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UserIcon = ({ size = 22, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const LogOutIcon = ({ size = 18, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 17L21 12L16 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 12H9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Human Capital (Users) icon
const UsersIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Dark mode toggle icons
const SunIcon = ({ size = 20, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 2V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4.93 4.93L6.34 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17.66 17.66L19.07 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 12H4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6.34 17.66L4.93 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19.07 4.93L17.66 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MoonIcon = ({ size = 20, color = '#374151' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 12.79C20.8427 14.4922 20.2039 16.1144 19.1583 17.4668C18.1127 18.8192 16.7035 19.8458 15.0957 20.4265C13.4879 21.0073 11.748 21.1181 10.0795 20.7461C8.41103 20.3741 6.88302 19.5345 5.67425 18.3258C4.46548 17.117 3.62596 15.589 3.25393 13.9205C2.88189 12.252 2.99274 10.5121 3.57348 8.9043C4.15423 7.29651 5.18085 5.88737 6.53324 4.84175C7.88562 3.79614 9.5078 3.15731 11.21 3C10.2134 4.34827 9.73387 6.00945 9.85857 7.68141C9.98328 9.35338 10.7039 10.9251 11.8894 12.1106C13.0749 13.2961 14.6466 14.0167 16.3186 14.1414C17.9906 14.2661 19.6517 13.7866 21 12.79Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Strategy Dashboard Icon (clipboard with checkmark)
const ClipboardCheckIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 5H7C6.46957 5 5.96086 5.21071 5.58579 5.58579C5.21071 5.96086 5 6.46957 5 7V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V7C19 6.46957 18.7893 5.96086 18.4142 5.58579C18.0391 5.21071 17.5304 5 17 5H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 5C9 4.46957 9.21071 3.96086 9.58579 3.58579C9.96086 3.21071 10.4696 3 11 3H13C13.5304 3 14.0391 3.21071 14.4142 3.58579C14.7893 3.96086 15 4.46957 15 5C15 5.53043 14.7893 6.03914 14.4142 6.41421C14.0391 6.78929 13.5304 7 13 7H11C10.4696 7 9.96086 6.78929 9.58579 6.41421C9.21071 6.03914 9 5.53043 9 5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 14L11 16L15 12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Navigation items configuration with icons and permissions
// ============================================================

type NavItem = {
  key: string;
  labelKey: string; // Translation key from navigation section
  icon: React.FC<{ size?: number; color?: string }>;
  allowedRoles?: string[];
  requiresTalent?: boolean;
  targetScreen?: string;
  targetStack?: string;
};

// ============================================================
// SMS PERMISSION RULES for sidebar navigation:
// - superuser (مسؤول النظام): ADMIN ONLY - Organizations + User Management
// - strategy_team: Dashboard, Organizations, Goals, Initiatives, Risks, Export, Help (NO Admin access)
// - executive (متخذ القرار): Uses StrategyStack (VIEW-ONLY) - same screens but no add/edit/delete/approve buttons
// - org_employee: Dashboard, MyOrganization, Goals, Initiatives, UnitGoals, Processes, Services, Risks, Help
// Golden Rule: If user cannot DO something → user cannot SEE it
// ============================================================

const navigationItems: NavItem[] = [
  // ============================================================
  // SUPERUSER SIDEBAR (Admin Interface):
  // 1. الجهات (Organizations) - Add/Edit/Delete organizations
  // 2. إدارة المستخدمين (User Management) - Add/Edit/Delete users
  // ============================================================

  // ============================================================
  // STRATEGY TEAM / EXECUTIVE SIDEBAR (per Figma design):
  // executive uses SAME StrategyStack but with VIEW-ONLY permissions
  // 1. لوحة المعلومات (StrategyStack)
  // 2. الجهات (Organizations)
  // 3. الأهداف الاستراتيجية (Goals)
  // 4. المبادرات الاستراتيجية (Initiatives)
  // 5. المخاطر (Risks)
  // 6. جدولة القراءات (Schedules)
  // 7. تصدير البيانات (Export) - strategy_team only
  // 8. الإدارة (Admin) - strategy_team only
  // ============================================================

  // ---- strategy_team / executive Dashboard → StrategyStack ----
  // executive uses same StrategyStack but screens hide add/edit/delete buttons
  {
    key: 'StrategyStack',
    labelKey: 'navigation.dashboard',
    icon: LayoutGridIcon,
    allowedRoles: ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver'],
    targetScreen: 'StrategyDashboard',
    targetStack: 'StrategyStack',
  },

  // ---- org_employee and org_executive Dashboard → DashboardStack ----
  {
    key: 'DashboardStack',
    labelKey: 'navigation.dashboard',
    icon: LayoutGridIcon,
    allowedRoles: ['org_employee', 'org_executive'],
  },

  // ---- Evaluation: تقييم الجهات (dashboard view) ----
  // Visible only to evaluation roles. Strategy/org users no longer see this
  // entry in the drawer.
  {
    key: 'EvaluationStack',
    labelKey: 'navigation.evaluationDashboard',
    icon: StarIcon,
    allowedRoles: ['performance_evaluator', 'evaluation_approver'],
    targetScreen: 'EvaluationDashboard',
    targetStack: 'EvaluationStack',
  },

  // ---- Evaluation: إدارة التقييم (framework management — evaluator + approver) ----
  // Approver sees the same screen but with row-level approve/return actions on
  // frameworks whose status is pending_approval.
  {
    key: 'EvaluationManagementNav',
    labelKey: 'navigation.evaluationManagement',
    icon: SettingsIcon,
    allowedRoles: ['superuser', 'performance_evaluator', 'evaluation_approver'],
    targetScreen: 'EvaluationManagement',
    targetStack: 'EvaluationStack',
  },

  // ---- org_employee and org_executive: Their own organization view ----
  {
    key: 'MyOrganizationStack',
    labelKey: 'navigation.myOrganization',
    icon: BuildingIcon,
    allowedRoles: ['org_employee', 'org_executive'],
  },

  // ---- Organizations: superuser (full CRUD), strategy_team, executive (view-only) ----
  {
    key: 'StrategyOrganizationsStack',
    labelKey: 'navigation.organizations',
    icon: BuildingsIcon,
    allowedRoles: ['superuser', 'strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver'],
  },

  // ---- Pending Approvals: org_executive and superuser ----
  {
    key: 'PendingApprovalsNav',
    labelKey: 'navigation.pendingApprovals',
    icon: ClipboardCheckIcon,
    allowedRoles: ['org_executive', 'superuser'],
    targetStack: 'StrategyStack',
    targetScreen: 'PendingApprovals',
  },

  // ---- Goals (strategy_team, executive, org_executive, and org_employee - NOT superuser) ----
  {
    key: 'GoalsStack',
    labelKey: 'navigation.strategicGoals',
    icon: TargetIcon,
    allowedRoles: ['strategy_team', 'org_employee', 'org_executive', 'executive', 'performance_evaluator', 'evaluation_approver'],
  },

  // ---- Initiatives: strategy_team, executive, org_executive, and org_employee ----
  {
    key: 'InitiativesStack',
    labelKey: 'navigation.strategicInitiatives',
    icon: RocketIcon,
    allowedRoles: ['strategy_team', 'org_executive', 'executive', 'performance_evaluator', 'evaluation_approver'],
  },
  {
    key: 'InitiativesStack',
    labelKey: 'navigation.strategicInitiatives',
    icon: RocketIcon,
    allowedRoles: ['org_employee'],
  },

  // ---- KPIs - HIDDEN from sidebar (access through Goals/Initiatives instead) ----
  // {
  //   key: 'KPIsStack',
  //   labelKey: 'navigation.kpis',
  //   icon: ChartBarIcon,
  //   allowedRoles: ['org_employee'],
  // },

  // ---- org_employee and org_executive: Unit Goals ----
  {
    key: 'UnitGoalsStack',
    labelKey: 'navigation.unitGoals',
    icon: GitBranchIcon,
    allowedRoles: ['org_employee', 'org_executive'],
  },
  {
    key: 'ProcessesStack',
    labelKey: 'navigation.processes',
    icon: WorkflowIcon,
    allowedRoles: ['strategy_team', 'org_employee', 'org_executive', 'executive', 'performance_evaluator', 'evaluation_approver'],
  },
  {
    key: 'ServicesStack',
    labelKey: 'navigation.services',
    icon: HandServicesIcon,
    allowedRoles: ['strategy_team', 'org_employee', 'org_executive', 'executive'],
  },

  // ---- Risks (strategy_team, executive, org_executive, and org_employee - NOT superuser) ----
  {
    key: 'RisksStack',
    labelKey: 'navigation.risks',
    icon: TriangleAlertIcon,
    allowedRoles: ['strategy_team', 'org_employee', 'org_executive', 'executive', 'performance_evaluator', 'evaluation_approver'],
  },

  // ---- Reading Schedules Management (strategy_team and executive) ----
  {
    key: 'SchedulesManagement',
    labelKey: 'navigation.schedules',
    icon: CalendarIcon,
    allowedRoles: ['strategy_team', 'executive'],
    targetScreen: 'SchedulesManagement',
    targetStack: 'StrategyStack',
  },

  // ---- Human Capital (org_employee and org_executive) ----
  {
    key: 'EmployeesStack',
    labelKey: 'navigation.humanCapital',
    icon: UsersIcon,
    allowedRoles: ['org_employee', 'org_executive'],
  },

  // ---- Talents Initiative (مبادرة مواهب) ----
  {
    key: 'TalentsStack',
    labelKey: 'navigation.talents',
    icon: TalentsIcon,
    allowedRoles: ['org_employee', 'org_executive', 'strategy_team', 'executive', 'coordinator', 'student'],
    requiresTalent: true,
  },

  // ---- Student: My Profile (ملفي الشخصي) ----
  {
    key: 'StudentProfileNav',
    labelKey: 'navigation.myProfile',
    icon: UserIcon,
    allowedRoles: ['student'],
    targetScreen: 'StudentProfile',
    targetStack: 'TalentsStack',
    requiresTalent: true,
  },

  // ---- Coordinator / Org Employee: Profile (الملف الشخصي) ----
  {
    key: 'CoordinatorProfileNav',
    labelKey: 'navigation.coordinatorProfile',
    icon: UserIcon,
    allowedRoles: ['coordinator'],
    targetScreen: 'CoordinatorProfile',
    targetStack: 'TalentsStack',
    requiresTalent: true,
  },

  // ---- strategy_team and executive: Reports (التقارير) ----
  {
    key: 'ExportStack',
    labelKey: 'navigation.reports',
    icon: FileChartIcon,
    allowedRoles: ['strategy_team', 'executive'],
  },

  // ---- User Management: superuser ONLY (Admin panel) ----
  {
    key: 'AdminStack',
    labelKey: 'navigation.userManagement',
    icon: UserIcon,
    allowedRoles: ['superuser'],
  },

  // ---- Settings/Administration: org_employee, org_executive, executive, strategy_team ----
  {
    key: 'SettingsStack',
    labelKey: 'navigation.admin',
    icon: SettingsIcon,
    allowedRoles: ['org_employee', 'org_executive', 'executive', 'strategy_team', 'coordinator'],
  },

  // ---- Settings: evaluator + approver (last entry for these roles) ----
  {
    key: 'EvaluationSettingsStack',
    labelKey: 'navigation.settings',
    icon: SettingsIcon,
    allowedRoles: ['performance_evaluator', 'evaluation_approver'],
    targetScreen: 'Settings',
    targetStack: 'SettingsStack',
  },

  // ---- Help: all roles (categories inside HelpScreen are role-filtered) ----
  {
    key: 'HelpStack',
    labelKey: 'navigation.help',
    icon: CircleHelpIcon,
    allowedRoles: [
      'org_employee', 'org_executive', 'executive', 'strategy_team',
      'performance_evaluator', 'evaluation_approver',
    ],
  },
];

export default function CustomDrawerContent(props: DrawerContentComponentProps) {
  const { user, logout } = useAuth();
  const { showSuccess } = useNotification();
  const { colors, isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  // Optimistic click target — set instantly on click so the highlight appears
  // before nested-route state or the URL have caught up. Cleared once react-
  // navigation reports the matching nested route.
  const [optimisticTarget, setOptimisticTarget] = useState<{
    stack: string;
    screen: string;
  } | null>(null);
  const { width } = useWindowDimensions();

  // Responsive: detect if drawer is in overlay mode (matches MainNavigator breakpoint)
  const isOverlayDrawer = width < 768;

  // Get current route name and nested screen name
  const currentRoute = props.state.routes[props.state.index]?.name;
  const nestedState = props.state.routes[props.state.index]?.state as any;
  const nestedRouteName = nestedState?.routes?.[nestedState?.index]?.name;

  // Clear the optimistic click target once react-navigation has caught up
  // (either the nested route matches the click, or the user navigated away).
  React.useEffect(() => {
    if (!optimisticTarget) return;
    const reachedTarget =
      currentRoute === optimisticTarget.stack &&
      nestedRouteName === optimisticTarget.screen;
    const navigatedElsewhere =
      currentRoute !== optimisticTarget.stack && nestedRouteName !== undefined;
    if (reachedTarget || navigatedElsewhere) {
      setOptimisticTarget(null);
    }
  }, [currentRoute, nestedRouteName, optimisticTarget]);

  // Drop any lingering focus on a drawer button when the route changes (e.g.
  // after browser back). Without this, the previously-clicked nav item keeps
  // its :focus ring even though the URL has moved on.
  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      typeof active.blur === 'function' &&
      // Only blur if the focused element looks like a drawer button (role=button).
      // Avoids stealing focus from inputs the user might still be typing in.
      active.getAttribute('role') === 'button'
    ) {
      active.blur();
    }
  }, [currentRoute, nestedRouteName]);

  // Get user role
  const userRole = user?.profile?.user_type || '';

  // Check if user has permission to see a nav item
  // SMS Permission Rules:
  // - superuser: ONLY sees items where 'superuser' is explicitly in allowedRoles (Admin interface: الجهات + إدارة المستخدمين)
  // - other roles: must be explicitly listed in allowedRoles
  // - allowedRoles undefined → everyone can see (except superuser who has restricted admin view)
  const hasPermission = (item: NavItem): boolean => {
    // If no allowedRoles specified (undefined), everyone except superuser can see
    // (superuser has a restricted admin-only interface)
    if (item.allowedRoles === undefined) {
      return userRole !== 'superuser';
    }

    // If allowedRoles is empty array [], no one can see
    if (item.allowedRoles.length === 0) {
      return false;
    }

    // Check if user's role is explicitly in the allowed list
    if (!item.allowedRoles.includes(userRole)) return false;

    // Check talent access for items that require it
    if (item.requiresTalent && !user?.talent_enabled) return false;

    return true;
  };

  // Get the set of stack names actually registered in the Drawer navigator
  const registeredStacks = new Set(
    props.state.routes.map((r: any) => r.name)
  );

  // Filter navigation items based on user permissions, available stacks, and remove duplicates
  const visibleNavItems = navigationItems
    .filter(hasPermission)
    .filter((item) => {
      // Only show items whose target stack is registered in the Drawer
      const targetStack = item.targetStack || item.key;
      return registeredStacks.has(targetStack);
    })
    .filter((item, index, self) =>
      // Remove duplicates by keeping only the first occurrence of each key or labelKey
      self.findIndex(i => i.key === item.key || i.labelKey === item.labelKey) === index
    );

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    await logout();
    showSuccess(t('auth.logoutSuccess'));
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Get user type display with translation
  const getUserTypeDisplay = () => {
    const typeKeys: Record<string, string> = {
      strategy_team: 'userTypes.strategyTeam',
      org_employee: 'userTypes.orgEmployee',
      org_executive: 'userTypes.orgExecutive',
      executive: 'userTypes.executive',
      superuser: 'userTypes.superuser',
      coordinator: 'userTypes.coordinator',
      student: 'userTypes.student',
    };
    const typeKey = typeKeys[user?.profile?.user_type || ''];
    return typeKey ? t(typeKey) : user?.profile?.user_type_display || t('userTypes.orgEmployee');
  };

  // Map each drawer stack to its root screen name
  const stackRootScreens: Record<string, string> = {
    DashboardStack: 'Dashboard',
    MyOrganizationStack: 'MyOrganization',
    OrganizationsStack: 'Organizations',
    GoalsStack: 'Goals',
    InitiativesStack: 'Initiatives',
    UnitGoalsStack: 'UnitGoals',
    ProcessesStack: 'Processes',
    ServicesStack: 'Services',
    RisksStack: 'Risks',
    EmployeesStack: 'Employees',
    TalentsStack: 'Talents',
    ExportStack: 'Export',
    AdminStack: 'Admin',
    HelpStack: 'Help',
    KPIsStack: 'KPIs',
    SchedulesStack: 'Schedules',
    NotificationsStack: 'Notifications',
    ProfileStack: 'Profile',
    SettingsStack: 'Settings',
    SearchStack: 'SearchResults',
    StrategyOrganizationsStack: 'StrategyOrganizations',
    StrategyStack: 'StrategyDashboard',
    EvaluationStack: 'EvaluationDashboard',
  };

  const handleNavigation = (item: any) => {
    const targetStack = item.targetStack || item.key;
    const targetScreen = item.targetScreen || stackRootScreens[item.key];

    // Optimistically mark this item as the active drawer entry so the
    // highlight is immediate; gets reconciled by the effect below once
    // react-navigation publishes the new nested route.
    if (targetStack && targetScreen) {
      setOptimisticTarget({ stack: targetStack, screen: targetScreen });
    }

    const state = props.navigation.getState();
    const currentStackName = state.routes[state.index]?.name;
    const switchingStacks = currentStackName !== targetStack;

    // react-navigation's web history adapter calls replaceState (not push)
    // when only the focused drawer screen changes — the drawer router's
    // `history` array tracks open/close status, not focused-route order, so
    // historyDelta comes out 0. The result is that pressing browser ← after
    // clicking a sidebar item exits the app entirely instead of going back
    // to the previous drawer screen.
    //
    // Workaround: manually push the CURRENT URL with a FRESH state id into
    // browser history before dispatching the navigate. react-navigation will
    // then replaceState on top of the freshly-pushed entry (overwriting the
    // duplicate, not the original), leaving the previous drawer screen's URL
    // preserved one step back. Using a fresh id (not the current one) keeps
    // react-navigation's internal items array consistent — its `replace`
    // resets the array when the id is unknown and that's what we want here.
    if (
      switchingStacks &&
      Platform.OS === 'web' &&
      typeof window !== 'undefined'
    ) {
      const freshId =
        'drawer-back-' +
        Date.now().toString(36) +
        '-' +
        Math.random().toString(36).slice(2, 9);
      window.history.pushState(
        { id: freshId },
        '',
        window.location.pathname + window.location.search,
      );
    }

    // navigate to the drawer route (and the stack's root screen). Native-stack
    // pops back to an existing route by name, so clicking a sidebar item from
    // /services/123 still lands on /services rather than pushing a fresh copy.
    props.navigation.dispatch(
      CommonActions.navigate({
        name: targetStack,
        params: targetScreen
          ? { screen: targetScreen, params: {} }
          : undefined,
      })
    );

    // Close drawer on mobile after navigation
    if (isOverlayDrawer) {
      props.navigation.closeDrawer();
    }
  };

  // Dynamic theme styles for sidebar
  const sidebarTheme = {
    container: {
      backgroundColor: isDark ? colors.card : '#FFFFFF',
      borderLeftColor: colors.cardBorder,
    },
    header: {
      backgroundColor: isDark ? colors.card : '#FFFFFF',
      borderBottomColor: colors.cardBorder,
    },
    userSection: {
      borderBottomColor: colors.cardBorder,
    },
    userType: {
      color: colors.textPrimary,
    },
    userEmail: {
      color: isDark ? colors.info : '#3B82F6',
    },
    dropdownMenu: {
      backgroundColor: colors.card,
      borderColor: colors.cardBorder,
    },
    navLabel: {
      color: colors.textSecondary,
    },
    navLabelActive: {
      color: colors.primary,
    },
  };

  return (
    <View style={[styles.container, sidebarTheme.container]}>
      {/* Header with Full Horizontal Logo - switches based on theme */}
      <View style={[styles.header, sidebarTheme.header]}>
        {/* Close button for mobile overlay drawer */}
        {isOverlayDrawer && (
          <TouchableOpacity
            style={styles.closeButton}
            onPress={() => props.navigation.closeDrawer()}
            activeOpacity={0.7}
          >
            <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
              <Path d="M18 6L6 18" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M6 6L18 18" stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
          </TouchableOpacity>
        )}
        <Image
          source={isDark ? logoHorizontalWhite : logoHorizontal}
          style={styles.logoHorizontal}
          resizeMode="contain"
        />
      </View>

      {/* User Info Section with Dropdown */}
      <View style={[styles.userSection, sidebarTheme.userSection]}>
        <TouchableOpacity
          style={styles.userRow}
          onPress={() => setMenuVisible(!menuVisible)}
          activeOpacity={0.7}
        >
          <EllipsisVerticalIcon size={18} color={colors.textLight} />
          <View style={styles.userInfo}>
            <Text style={[styles.userType, sidebarTheme.userType]} numberOfLines={1}>{getUserTypeDisplay()}</Text>
            <Text style={[styles.userEmail, { color: sidebarTheme.userEmail.color }]} numberOfLines={1}>{user?.email || 'mail@mail.com'}</Text>
          </View>
          <View style={[styles.userIcon, { backgroundColor: isDark ? colors.backgroundAlt : '#F5F5F5' }]}>
            <UserIcon size={22} color={colors.iconMuted} />
          </View>
        </TouchableOpacity>

        {/* Dropdown Menu */}
        {menuVisible && (
          <View style={[styles.dropdownMenu, sidebarTheme.dropdownMenu]}>
            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={() => {
                setMenuVisible(false);
                setShowLogoutModal(true);
              }}
            >
              <Text style={styles.dropdownItemText}>{t('auth.logout')}</Text>
              <LogOutIcon size={16} color={colors.danger} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Navigation Items - filtered by user permissions */}
      <ScrollView style={styles.navScrollView} showsVerticalScrollIndicator={false}>
        {(() => {
          // On web, the URL is the most reliable signal of what's actually
          // rendered — react-navigation may not populate the nested route
          // synchronously on a deep-link refresh. We use the URL pathname
          // as a fallback when nestedRouteName is undefined.
          //
          // This map mirrors the linking config in `navigation/linking.ts`
          // for the drawer items that share a stack (multi-item stacks).
          const itemFullPath: Record<string, string> = {
            // EvaluationStack
            EvaluationStack_EvaluationDashboard: '/evaluation',
            EvaluationStack_EvaluationManagement: '/evaluation/manage',
            // TalentsStack
            TalentsStack_StudentProfile: '/talents/my-profile',
            TalentsStack_CoordinatorProfile: '/talents/coordinator-profile',
          };
          const webPath =
            Platform.OS === 'web' && typeof window !== 'undefined'
              ? window.location.pathname.replace(/\/$/, '') || '/'
              : '';

          return visibleNavItems.map((item) => {
            // Active-state resolution per item:
            // 1) Optimistic click target — set instantly when this exact item was
            //    just clicked. Wins over everything else, gets cleared when nested
            //    state catches up.
            // 2) react-navigation's nested route (works on click + most refreshes).
            // 3) For items in multi-item stacks (listed in itemFullPath), strict
            //    URL match. No stack-root fallback, or siblings would all light up.
            // 4) For single-item stacks, fall back to the stack's root screen so
            //    the first-click highlight works before nested state mounts.
            const isOptimisticHit =
              !!optimisticTarget &&
              optimisticTarget.stack === item.targetStack &&
              optimisticTarget.screen === item.targetScreen;

            const itemPathKey = `${item.targetStack ?? ''}_${item.targetScreen ?? ''}`;
            const expectedPath = itemFullPath[itemPathKey];
            // Resolve a deep nested route (e.g. EvaluationDetail, FrameworkBuilder)
            // to the drawer-item screen it logically belongs under, so the
            // highlight follows the user into descendant pages.
            const childToParentScreen: Record<string, string> = {
              EvaluationDashboard: 'EvaluationDashboard',
              EvaluationDetail: 'EvaluationDashboard',
              CriteriaScoringForm: 'EvaluationDashboard',
              EvaluationReadOnly: 'EvaluationDashboard',
              AnnualEvalTable: 'EvaluationDashboard',
              MonthlyEvalTable: 'EvaluationDashboard',
              EvaluationManagement: 'EvaluationManagement',
              FrameworkBuilder: 'EvaluationManagement',
            };
            let effectiveNested: string | undefined = nestedRouteName;
            if (effectiveNested && childToParentScreen[effectiveNested]) {
              effectiveNested = childToParentScreen[effectiveNested];
            }
            if (effectiveNested === undefined) {
              if (expectedPath !== undefined) {
                effectiveNested = webPath === expectedPath ? item.targetScreen : undefined;
              } else {
                effectiveNested = stackRootScreens[currentRoute ?? ''];
              }
            }
            const isActive =
              isOptimisticHit ||
              (item.targetStack
                ? (currentRoute === item.targetStack && effectiveNested === item.targetScreen)
                : (currentRoute === item.key && !navigationItems.some(n => n.targetStack === item.key && n.targetScreen === effectiveNested)));
          const IconComponent = item.icon;
          return (
            <TouchableOpacity
              key={item.key}
              style={[
                styles.navItem,
                isActive && styles.navItemActive,
                isActive && { backgroundColor: colors.primaryLight },
              ]}
              onPress={() => {
                // Drop the leftover :focus on web so a stale focus ring doesn't
                // linger after browser-back or programmatic navigation away.
                if (Platform.OS === 'web' && typeof document !== 'undefined') {
                  const active = document.activeElement as HTMLElement | null;
                  if (active && typeof active.blur === 'function') active.blur();
                }
                handleNavigation(item);
              }}
            >
              <Text style={[
                styles.navLabel,
                { color: colors.textSecondary },
                isActive && styles.navLabelActive,
                isActive && { color: colors.primary },
              ]}>
                {t(item.labelKey)}
              </Text>
              <View style={styles.navIconContainer}>
                <IconComponent
                  size={20}
                  color={isActive ? colors.primary : colors.iconMuted}
                />
              </View>
            </TouchableOpacity>
          );
          });
        })()}
      </ScrollView>

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        visible={showLogoutModal}
        title={t('auth.logout')}
        message={t('auth.logoutConfirm')}
        confirmText={t('auth.logout')}
        cancelText={t('common.cancel')}
        type="danger"
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderLeftWidth: 1,
    borderLeftColor: '#E5E5E5',
  },
  header: {
    backgroundColor: COLORS.white,
    paddingTop: 16,
    paddingBottom: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
    alignItems: 'center',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoHorizontal: {
    width: '100%',
    height: 70,
  },
  userSection: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
    position: 'relative',
    zIndex: 100,
  },
  userRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 10,
    right: 10,
    marginTop: 4,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 10,
    zIndex: 1000,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  dropdownItemText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#EF4444',
  },
  userInfo: {
    flex: 1,
    alignItems: 'flex-end',
    marginRight: 8,
    overflow: 'hidden',
  },
  userType: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: COLORS.neutral800,
    textAlign: 'right',
  },
  userEmail: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#3B82F6',
    textAlign: 'right',
    marginTop: 2,
  },
  userIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  navScrollView: {
    flex: 1,
    paddingTop: 6,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: 32,
    paddingVertical: 4,
    paddingHorizontal: 10,
    marginHorizontal: 6,
    marginVertical: 1,
    borderRadius: 6,
    gap: 6,
  },
  navItemActive: {
    backgroundColor: '#FEF2F2',
  },
  navLabel: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#374151',
    flexShrink: 1,
  },
  navLabelActive: {
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  navIconContainer: {
    width: 22,
    height: 22,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  themeToggleContainer: {
    paddingHorizontal: 10,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  themeToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
  },
  themeToggleText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#374151',
  },
});
